import React, { useState, useEffect } from 'react';
import { C } from './tokens';
import { api } from './api';
import { Toaster, toast } from
  'react-hot-toast';

// ── AUTH ──────────────────────────────────────

function useAuth() {
  const [authed, setAuthed] = useState(
    !!localStorage.getItem('sf_parent'));
  const [fam, setFam] = useState(
    localStorage.getItem('sf_fam') || '');

  const login = (pt: string, fn: string) => {
    localStorage.setItem('sf_parent', pt);
    localStorage.setItem('sf_fam', fn);
    setAuthed(true); setFam(fn);
  };
  const logout = () => {
    localStorage.clear();
    setAuthed(false); setFam('');
  };
  return { authed, fam, login, logout };
}

// ── STYLES ────────────────────────────────────

const s = {
  page: {
    minHeight: '100vh',
    background: C.bg,
    fontFamily: '-apple-system,BlinkMacSystemFont,sans-serif',
  } as React.CSSProperties,
  card: {
    background: C.card,
    borderRadius: 16,
    border: `0.5px solid ${C.border}`,
    padding: 20,
  } as React.CSSProperties,
  btn: (bg=C.blue,fg='#fff') => ({
    background: bg,
    color: fg,
    border: 'none',
    borderRadius: 12,
    padding: '10px 20px',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
  } as React.CSSProperties),
  input: {
    width: '100%',
    padding: '12px 14px',
    borderRadius: 12,
    border: `1.5px solid ${C.border}`,
    fontSize: 14,
    color: C.navy,
    background: C.card,
    outline: 'none',
    boxSizing: 'border-box',
  } as React.CSSProperties,
  label: {
    fontSize: 12,
    fontWeight: 700,
    color: C.gray,
    marginBottom: 6,
    display: 'block',
  } as React.CSSProperties,
};

// ── LOGO ──────────────────────────────────────

const Logo = ({size=32,dark=false}: any) => (
  <div style={{display:'flex',
    alignItems:'center',gap:10}}>
    <div style={{width:size,height:size,
      borderRadius:'50%',background:C.green,
      display:'flex',alignItems:'center',
      justifyContent:'center'}}>
      <svg width={size*0.55} height={size*0.55}
           viewBox="0 0 20 20" fill="none">
        <path d="M10 2L11.8 7.2H17.6L12.8
          10.4L14.6 15.6L10 12.4L5.4 15.6
          L7.2 10.4L2.4 7.2H8.2L10 2Z"
          fill="white"/>
      </svg>
    </div>
    <div>
      <div style={{fontSize:size*0.5,
        fontWeight:800,
        color:dark?'#F8FAFC':C.navy,
        lineHeight:1.1}}>SuperFit</div>
      <div style={{fontSize:size*0.38,
        fontWeight:500,color:C.green,
        lineHeight:1}}>Family</div>
    </div>
  </div>
);

// ── LOGIN ─────────────────────────────────────

function Login({onLogin}:{
    onLogin:(t:string,f:string)=>void}) {
  const [pin,setPin] = useState('');
  const [loading,setLoading] = useState(false);
  const [err,setErr] = useState('');

  const tap = (n: string) => {
    if(n==='⌫'){setPin(p=>p.slice(0,-1));return;}
    if(pin.length>=4) return;
    const np = pin+n;
    setPin(np);
    if(np.length===4) submit(np);
  };

  const submit = async (p: string) => {
    setLoading(true); setErr('');
    try {
      const r = await api.pin(p);
      const pt = r.data.parent_token;
      const famName =
        localStorage.getItem('sf_fam_name') ||
        'Your Family';
      onLogin(pt, famName);
    } catch {
      setErr('Incorrect PIN. Please try again.');
      setPin('');
    } finally { setLoading(false); }
  };

  return (
    <div style={{...s.page,display:'flex',
      alignItems:'center',
      justifyContent:'center',padding:24}}>
      <div style={{...s.card,width:'100%',
        maxWidth:380,textAlign:'center',
        padding:32}}>
        <div style={{marginBottom:24}}>
          <Logo size={40}/>
        </div>
        <h2 style={{fontSize:22,fontWeight:800,
          color:C.navy,marginBottom:6}}>
          Parent Dashboard
        </h2>
        <p style={{fontSize:13,color:C.gray,
          marginBottom:28,lineHeight:1.6}}>
          Enter your 4-digit PIN to continue.
          <br/>Set up on the mobile app first.
        </p>

        {/* PIN dots */}
        <div style={{display:'flex',
          justifyContent:'center',gap:12,
          marginBottom:24}}>
          {[0,1,2,3].map(i=>(
            <div key={i} style={{width:14,
              height:14,borderRadius:'50%',
              background:pin.length>i
                ?C.blue:C.border,
              transition:'background 0.15s'}}/>
          ))}
        </div>

        {/* Keypad */}
        <div style={{display:'grid',
          gridTemplateColumns:'repeat(3,1fr)',
          gap:10,marginBottom:16}}>
          {[1,2,3,4,5,6,7,8,9,'',0,'⌫']
            .map((n,i)=>(
            <button key={i}
              onClick={()=>n!==''&&tap(
                String(n))}
              disabled={loading}
              style={{
                ...s.btn(n===''
                  ?'transparent':C.bg,C.navy),
                height:52,fontSize:18,
                border:`0.5px solid ${
                  n===''?'transparent':C.border}`,
              }}>
              {loading&&pin.length===4
                &&n===''?'...':n}
            </button>
          ))}
        </div>

        {err&&<p style={{fontSize:12,
          color:'#DC2626',marginBottom:8}}>
          {err}</p>}

        <a href="https://app.superfitfamily.in"
           target="_blank" rel="noreferrer"
           style={{fontSize:12,color:C.blue,
             textDecoration:'none'}}>
          Don't have an account?
          Set up on the mobile app →
        </a>
      </div>
    </div>
  );
}

// ── SIDEBAR ───────────────────────────────────

const NAV = [
  {id:'dash',icon:'🏠',label:'Dashboard'},
  {id:'children',icon:'👶',label:'Children'},
  {id:'habits',icon:'✔',label:'Habits'},
  {id:'progress',icon:'📊',label:'Progress'},
  {id:'rewards',icon:'🎁',label:'Rewards'},
  {id:'report',icon:'📄',label:'Report Card'},
  {id:'settings',icon:'⚙️',label:'Settings'},
];

function Sidebar({page,setPage,fam,logout}:any) {
  return (
    <div style={{width:240,background:C.navy,
      minHeight:'100vh',display:'flex',
      flexDirection:'column',
      padding:'24px 0',flexShrink:0}}>
      <div style={{padding:'0 20px 24px'}}>
        <Logo size={28} dark/>
        <div style={{fontSize:11,color:'#475569',
          marginTop:8,fontWeight:600,
          letterSpacing:'0.05em'}}>
          {fam || 'Family Dashboard'}
        </div>
      </div>
      <div style={{flex:1}}>
        {NAV.map(n=>(
          <button key={n.id}
            onClick={()=>setPage(n.id)}
            style={{
              display:'flex',
              alignItems:'center',
              gap:10,width:'100%',
              padding:'12px 20px',
              background:page===n.id
                ?'rgba(255,255,255,0.1)':'none',
              border:'none',
              borderLeft:page===n.id
                ?`4px solid ${C.green}`
                :'4px solid transparent',
              color:page===n.id
                ?'#F8FAFC':'#94A3B8',
              fontSize:14,fontWeight:
                page===n.id?700:400,
              cursor:'pointer',
              textAlign:'left',
            }}>
            <span>{n.icon}</span>
            <span>{n.label}</span>
          </button>
        ))}
      </div>
      <div style={{padding:'0 16px'}}>
        <button onClick={logout}
          style={{...s.btn('#DC2626'),
            width:'100%',fontSize:13}}>
          Logout
        </button>
      </div>
    </div>
  );
}

// ── STAT CARD ─────────────────────────────────

function StatCard({label,value,color=C.blue,
    icon}:any) {
  return (
    <div style={{...s.card}}>
      <div style={{fontSize:24,marginBottom:8}}>
        {icon}
      </div>
      <div style={{fontSize:28,fontWeight:800,
        color,marginBottom:4}}>{value}</div>
      <div style={{fontSize:12,color:C.gray}}>
        {label}
      </div>
    </div>
  );
}

// ── DASHBOARD PAGE ────────────────────────────

function DashboardPage() {
  const [data,setData] = useState<any>(null);
  const [children,setChildren] =
    useState<any[]>([]);
  const [loading,setLoading] = useState(true);

  useEffect(()=>{
    Promise.all([
      api.progress(),
      api.children(),
    ]).then(([p,c])=>{
      setData(p.data);
      setChildren(c.data.children||[]);
    }).catch(()=>{
      toast.error('Failed to load data');
    }).finally(()=>setLoading(false));
  },[]);

  if(loading) return (
    <div style={{display:'flex',
      alignItems:'center',
      justifyContent:'center',height:400}}>
      <div style={{fontSize:14,color:C.gray}}>
        Loading...</div>
    </div>
  );

  const score = data?.family_score || 0;
  const streak = data?.streak_weeks || 0;

  return (
    <div>
      <h1 style={{fontSize:24,fontWeight:800,
        color:C.navy,marginBottom:4}}>
        Dashboard
      </h1>
      <p style={{fontSize:13,color:C.gray,
        marginBottom:24}}>
        Your family's overview
      </p>

      {/* Stat cards */}
      <div style={{display:'grid',
        gridTemplateColumns:
          'repeat(auto-fit,minmax(180px,1fr))',
        gap:14,marginBottom:24}}>
        <StatCard icon="📊"
          label="Family Score"
          value={`${score}%`} color={C.blue}/>
        <StatCard icon="👶"
          label="Children"
          value={children.length}
          color={C.green}/>
        <StatCard icon="🔥"
          label="Streak (weeks)"
          value={streak} color={C.amber}/>
        <StatCard icon="🎯"
          label="This week"
          value="Active" color={C.rose}/>
      </div>

      {/* Children cards */}
      <div style={{...s.card,marginBottom:20}}>
        <h3 style={{fontSize:16,fontWeight:700,
          color:C.navy,marginBottom:16}}>
          Children this week
        </h3>
        {data?.children?.length===0&&(
          <p style={{color:C.gray,fontSize:13}}>
            No children yet. Add one in
            the Children tab.
          </p>
        )}
        <div style={{display:'flex',
          flexDirection:'column',gap:12}}>
          {(data?.children||[]).map((c:any)=>(
            <div key={c.child_id}
              style={{display:'flex',
                alignItems:'center',gap:12,
                padding:'12px',
                borderRadius:12,
                background:C.bg,
                border:`0.5px solid ${C.border}`}}>
              <div style={{fontSize:28}}>
                {c.avatar}
              </div>
              <div style={{flex:1}}>
                <div style={{fontSize:14,
                  fontWeight:700,color:C.navy}}>
                  {c.name}
                </div>
                <div style={{fontSize:12,
                  color:C.gray}}>
                  {c.identity_title}
                </div>
              </div>
              <div style={{fontSize:20,
                fontWeight:800,color:C.blue}}>
                {c.completion_rate}%
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Week data chart */}
      {data?.week_data&&(
        <div style={s.card}>
          <h3 style={{fontSize:16,fontWeight:700,
            color:C.navy,marginBottom:16}}>
            Habits this week
          </h3>
          <div style={{display:'flex',
            gap:8,alignItems:'flex-end',
            height:120}}>
            {data.week_data.map((d:any,i:number)=>(
              <div key={i} style={{flex:1,
                display:'flex',
                flexDirection:'column',
                alignItems:'center',gap:4}}>
                <div style={{display:'flex',
                  gap:2,alignItems:'flex-end',
                  height:96}}>
                  {(d.values||[0]).map(
                      (v:number,j:number)=>(
                    <div key={j} style={{
                      width:10,
                      borderRadius:'3px 3px 0 0',
                      background:j===0
                        ?C.blue:C.rose,
                      height:`${Math.max(2,
                        (v/5)*96)}px`,
                      transition:'height 0.4s',
                    }}/>
                  ))}
                </div>
                <div style={{fontSize:10,
                  color:C.grayL}}>
                  {d.day}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── CHILDREN PAGE ─────────────────────────────

function ChildrenPage() {
  const [children,setChildren] =
    useState<any[]>([]);
  const [loading,setLoading] = useState(true);
  const [name,setName] = useState('');
  const [age,setAge] = useState(8);
  const [avatar,setAvatar] = useState('👦');
  const [adding,setAdding] = useState(false);

  const load = () => {
    api.children().then(r=>{
      setChildren(r.data.children||[]);
    }).finally(()=>setLoading(false));
  };

  useEffect(()=>{ load(); },[]);

  const addChild = async () => {
    if(!name.trim()) return;
    setAdding(true);
    try {
      await api.createChild({
        name,age,avatar,
        jar_split:{giving:30,saving:50,
          spending:20},
      });
      toast.success(`${name} added!`);
      setName(''); setAge(8);
      load();
    } catch {
      toast.error('Failed to add child');
    } finally { setAdding(false); }
  };

  if(loading) return (
    <div style={{color:C.gray,padding:20}}>
      Loading...</div>
  );

  return (
    <div>
      <h1 style={{fontSize:24,fontWeight:800,
        color:C.navy,marginBottom:4}}>
        Children
      </h1>
      <p style={{fontSize:13,color:C.gray,
        marginBottom:24}}>
        Manage your children's profiles
      </p>

      {/* Children list */}
      <div style={{display:'grid',
        gridTemplateColumns:
          'repeat(auto-fill,minmax(220px,1fr))',
        gap:14,marginBottom:24}}>
        {children.map((c:any)=>(
          <div key={c.id} style={{...s.card}}>
            <div style={{fontSize:40,
              marginBottom:8}}>{c.avatar}</div>
            <div style={{fontSize:16,
              fontWeight:700,color:C.navy}}>
              {c.name}
            </div>
            <div style={{fontSize:12,
              color:C.gray,marginBottom:8}}>
              Age {c.age} · {c.identity_title}
            </div>
            <div style={{fontSize:11,
              color:C.blue,fontWeight:600}}>
              Jar split: {c.jar_split?.giving}%
              / {c.jar_split?.saving}%
              / {c.jar_split?.spending}%
            </div>
          </div>
        ))}
      </div>

      {/* Add child form */}
      <div style={{...s.card}}>
        <h3 style={{fontSize:16,fontWeight:700,
          color:C.navy,marginBottom:16}}>
          Add a child
        </h3>
        <div style={{display:'grid',
          gridTemplateColumns:'1fr 1fr',
          gap:14,marginBottom:14}}>
          <div>
            <label style={s.label}>Name</label>
            <input style={s.input}
              placeholder="Child's name"
              value={name}
              onChange={e=>setName(
                e.target.value)}/>
          </div>
          <div>
            <label style={s.label}>Age</label>
            <select style={s.input}
              value={age}
              onChange={e=>setAge(
                Number(e.target.value))}>
              {Array.from({length:13},
                (_,i)=>i+2).map(a=>(
                <option key={a} value={a}>
                  {a} years
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{marginBottom:14}}>
          <label style={s.label}>Avatar</label>
          <div style={{display:'flex',gap:8}}>
            {['👦','👧','🧒','👶'].map(a=>(
              <button key={a}
                onClick={()=>setAvatar(a)}
                style={{
                  fontSize:28,padding:8,
                  borderRadius:10,
                  border:`2px solid ${
                    avatar===a
                      ?C.green:C.border}`,
                  background:avatar===a
                    ?C.greenL:'white',
                  cursor:'pointer',
                }}>
                {a}
              </button>
            ))}
          </div>
        </div>
        <button style={s.btn()}
          onClick={addChild}
          disabled={adding||!name.trim()}>
          {adding?'Adding...':'Add Child'}
        </button>
      </div>
    </div>
  );
}

// ── HABITS PAGE ───────────────────────────────

function HabitsPage() {
  const [children,setChildren] =
    useState<any[]>([]);
  const [selChild,setSelChild] =
    useState<string>('');
  const [habits,setHabits] =
    useState<any[]>([]);
  const [name,setName] = useState('');
  const [icon,setIcon] = useState('✔');
  const [cat,setCat] = useState('health');
  const [pts,setPts] = useState(10);
  const [adding,setAdding] = useState(false);

  useEffect(()=>{
    api.children().then(r=>{
      const ch = r.data.children||[];
      setChildren(ch);
      if(ch.length>0) setSelChild(ch[0].id);
    });
  },[]);

  useEffect(()=>{
    if(!selChild) return;
    api.habits(selChild).then(r=>{
      setHabits(r.data.habits||[]);
    });
  },[selChild]);

  const addHabit = async () => {
    if(!name.trim()||!selChild) return;
    setAdding(true);
    try {
      await api.createHabit(selChild,{
        name,icon,category:cat,
        points_value:pts,
      });
      toast.success('Habit added!');
      setName('');
      api.habits(selChild).then(r=>
        setHabits(r.data.habits||[]));
    } catch {
      toast.error('Failed to add habit');
    } finally { setAdding(false); }
  };

  const deleteHabit = async (id:string,
      hname:string) => {
    if(!window.confirm(
        `Delete "${hname}"?`)) return;
    try {
      await api.deleteHabit(id);
      toast.success('Habit deleted');
      setHabits(h=>h.filter(x=>x.id!==id));
    } catch {
      toast.error('Failed to delete');
    }
  };

  const ICONS = ['🏃','🥛','📖','🏠','🥗',
    '✏️','🦷','💧','😴','🧸','🎵','🧘'];
  const CATS = ['health','mind',
    'values','finance'];

  return (
    <div>
      <h1 style={{fontSize:24,fontWeight:800,
        color:C.navy,marginBottom:4}}>
        Habits
      </h1>
      <p style={{fontSize:13,color:C.gray,
        marginBottom:24}}>
        Manage habits for each child
      </p>

      {/* Child selector */}
      <div style={{display:'flex',gap:8,
        marginBottom:20}}>
        {children.map((c:any)=>(
          <button key={c.id}
            onClick={()=>setSelChild(c.id)}
            style={{
              padding:'8px 16px',
              borderRadius:20,
              border:`2px solid ${
                selChild===c.id
                  ?C.blue:C.border}`,
              background:selChild===c.id
                ?C.blueL:'white',
              color:selChild===c.id
                ?C.blue:C.gray,
              fontSize:13,fontWeight:700,
              cursor:'pointer',
            }}>
            {c.avatar} {c.name}
          </button>
        ))}
      </div>

      {/* Habits list */}
      <div style={{...s.card,marginBottom:20}}>
        <h3 style={{fontSize:15,fontWeight:700,
          color:C.navy,marginBottom:14}}>
          Current habits
          ({habits.filter(h=>h.is_active)
            .length} active)
        </h3>
        {habits.length===0&&(
          <p style={{color:C.gray,fontSize:13}}>
            No habits yet. Add one below.
          </p>
        )}
        <div style={{display:'flex',
          flexDirection:'column',gap:8}}>
          {habits.filter(h=>h.is_active)
            .map((h:any)=>(
            <div key={h.id}
              style={{display:'flex',
                alignItems:'center',gap:10,
                padding:'10px 14px',
                borderRadius:10,
                background:C.bg,
                border:`0.5px solid ${C.border}`}}>
              <span style={{fontSize:20}}>
                {h.icon}
              </span>
              <div style={{flex:1}}>
                <div style={{fontSize:13,
                  fontWeight:600,color:C.navy}}>
                  {h.name}
                </div>
                <div style={{fontSize:11,
                  color:C.gray}}>
                  {h.category} · {h.points_value}pts
                </div>
              </div>
              <button
                onClick={()=>deleteHabit(
                  h.id,h.name)}
                style={{...s.btn('#FEF2F2',
                  '#DC2626'),
                  padding:'6px 12px',
                  fontSize:12}}>
                Delete
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Add habit form */}
      <div style={s.card}>
        <h3 style={{fontSize:15,fontWeight:700,
          color:C.navy,marginBottom:14}}>
          Add new habit
        </h3>
        <div style={{display:'grid',
          gridTemplateColumns:'1fr 1fr',
          gap:12,marginBottom:12}}>
          <div>
            <label style={s.label}>
              Habit name
            </label>
            <input style={s.input}
              placeholder="e.g. Morning exercise"
              value={name}
              onChange={e=>setName(
                e.target.value)}/>
          </div>
          <div>
            <label style={s.label}>
              Category
            </label>
            <select style={s.input}
              value={cat}
              onChange={e=>setCat(
                e.target.value)}>
              {CATS.map(c=>(
                <option key={c} value={c}>
                  {c.charAt(0).toUpperCase()
                    +c.slice(1)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{marginBottom:12}}>
          <label style={s.label}>Icon</label>
          <div style={{display:'flex',
            flexWrap:'wrap',gap:6}}>
            {ICONS.map(ic=>(
              <button key={ic}
                onClick={()=>setIcon(ic)}
                style={{
                  fontSize:22,padding:6,
                  borderRadius:8,
                  border:`2px solid ${
                    icon===ic
                      ?C.green:C.border}`,
                  background:icon===ic
                    ?C.greenL:'white',
                  cursor:'pointer',
                }}>
                {ic}
              </button>
            ))}
          </div>
        </div>
        <div style={{marginBottom:14}}>
          <label style={s.label}>
            Points: {pts}
          </label>
          <input type="range" min={5} max={20}
            step={5} value={pts}
            onChange={e=>setPts(
              Number(e.target.value))}
            style={{width:'100%',
              accentColor:C.amber}}/>
          <div style={{display:'flex',
            justifyContent:'space-between',
            fontSize:11,color:C.grayL}}>
            <span>5pts</span>
            <span>20pts</span>
          </div>
        </div>
        <button style={s.btn()}
          onClick={addHabit}
          disabled={adding||!name.trim()}>
          {adding?'Adding...':'Add Habit'}
        </button>
      </div>
    </div>
  );
}

// ── PROGRESS PAGE ─────────────────────────────

function ProgressPage() {
  const [data,setData] = useState<any>(null);
  const [loading,setLoading] = useState(true);

  useEffect(()=>{
    api.progress().then(r=>{
      setData(r.data);
    }).finally(()=>setLoading(false));
  },[]);

  if(loading) return (
    <div style={{color:C.gray,padding:20}}>
      Loading...</div>
  );

  return (
    <div>
      <h1 style={{fontSize:24,fontWeight:800,
        color:C.navy,marginBottom:4}}>
        Progress
      </h1>
      <p style={{fontSize:13,color:C.gray,
        marginBottom:24}}>
        Your family's weekly performance
      </p>

      {/* Big score */}
      <div style={{...s.card,
        textAlign:'center',marginBottom:20}}>
        <div style={{fontSize:13,
          color:C.gray,marginBottom:8}}>
          Family Discipline Score
        </div>
        <div style={{fontSize:64,
          fontWeight:800,color:C.blue,
          lineHeight:1}}>
          {data?.family_score||0}
          <span style={{fontSize:28,
            color:C.gray,fontWeight:400}}>
            %
          </span>
        </div>
        <div style={{fontSize:13,
          color:C.green,marginTop:8}}>
          🔥 {data?.streak_weeks||0} week
          honest streak
        </div>
      </div>

      {/* Per child */}
      <div style={{display:'grid',
        gridTemplateColumns:
          'repeat(auto-fit,minmax(200px,1fr))',
        gap:14,marginBottom:20}}>
        {(data?.children||[]).map((c:any)=>(
          <div key={c.child_id} style={s.card}>
            <div style={{display:'flex',
              alignItems:'center',gap:10,
              marginBottom:12}}>
              <span style={{fontSize:28}}>
                {c.avatar}
              </span>
              <div>
                <div style={{fontSize:14,
                  fontWeight:700,color:C.navy}}>
                  {c.name}
                </div>
                <div style={{fontSize:11,
                  color:C.gray}}>
                  {c.identity_title}
                </div>
              </div>
            </div>
            <div style={{fontSize:28,
              fontWeight:800,color:C.blue,
              marginBottom:8}}>
              {c.completion_rate}%
            </div>
            <div style={{height:8,
              borderRadius:4,
              background:C.border,
              overflow:'hidden'}}>
              <div style={{height:'100%',
                borderRadius:4,
                background:C.blue,
                width:`${c.completion_rate}%`}}/>
            </div>
            <div style={{display:'flex',
              gap:12,marginTop:12,
              fontSize:11,color:C.gray}}>
              <span>🏃 {c.exercise_days}d</span>
              <span>📖 {c.reading_days}d</span>
              <span>💛 {c.kindness_acts}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── REWARDS PAGE ──────────────────────────────

function RewardsPage() {
  const [rewards,setRewards] =
    useState<any[]>([]);
  const [pending,setPending] =
    useState<any[]>([]);
  const [name,setName] = useState('');
  const [icon,setIcon] = useState('🎁');
  const [pts,setPts] = useState(200);
  const [loading,setLoading] = useState(true);

  const load = () => {
    Promise.all([
      api.rewards(),
      api.pending(),
    ]).then(([r,p])=>{
      setRewards(r.data.rewards||[]);
      setPending(p.data.pending||[]);
    }).finally(()=>setLoading(false));
  };

  useEffect(()=>{ load(); },[]);

  const addReward = async () => {
    if(!name.trim()) return;
    try {
      await api.createReward({
        name,icon,cost_points:pts,
        jar_type:'spending',
      });
      toast.success('Reward added!');
      setName('');
      load();
    } catch {
      toast.error('Failed to add reward');
    }
  };

  const resolve = async (
      id:string, status:string,
      rname:string) => {
    try {
      await api.resolve(id, status);
      toast.success(status==='approved'
        ?`✓ Approved: ${rname}`
        :`Points refunded`);
      load();
    } catch {
      toast.error('Failed');
    }
  };

  if(loading) return (
    <div style={{color:C.gray,padding:20}}>
      Loading...</div>
  );

  const REWARD_ICONS = ['🎁','🍦','📱','🍽️',
    '📚','🌙','🎬','🌳','🎮','🏖️'];

  return (
    <div>
      <h1 style={{fontSize:24,fontWeight:800,
        color:C.navy,marginBottom:4}}>
        Rewards
      </h1>
      <p style={{fontSize:13,color:C.gray,
        marginBottom:24}}>
        Manage rewards and approve requests
      </p>

      {/* Pending */}
      {pending.length>0&&(
        <div style={{...s.card,
          marginBottom:20,
          border:`1.5px solid ${C.amber}`}}>
          <h3 style={{fontSize:15,fontWeight:700,
            color:C.amber,marginBottom:14}}>
            🔔 {pending.length} pending request
            {pending.length>1?'s':''}
          </h3>
          <div style={{display:'flex',
            flexDirection:'column',gap:10}}>
            {pending.map((p:any)=>(
              <div key={p.id}
                style={{display:'flex',
                  alignItems:'center',gap:12,
                  padding:'12px',
                  borderRadius:10,
                  background:C.amberL,
                  border:`0.5px solid ${
                    C.amber}40`}}>
                <span style={{fontSize:24}}>
                  {p.child_avatar}
                </span>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,
                    fontWeight:700,color:C.navy}}>
                    {p.child_name} wants{' '}
                    {p.reward_icon} {p.reward_name}
                  </div>
                  <div style={{fontSize:11,
                    color:C.gray}}>
                    {p.points_spent} points
                  </div>
                </div>
                <div style={{display:'flex',
                  gap:6}}>
                  <button
                    onClick={()=>resolve(
                      p.id,'approved',
                      p.reward_name)}
                    style={{...s.btn(C.green),
                      padding:'8px 14px',
                      fontSize:12}}>
                    ✓ Approve
                  </button>
                  <button
                    onClick={()=>resolve(
                      p.id,'rejected',
                      p.reward_name)}
                    style={{...s.btn('#FEF2F2',
                      '#DC2626'),
                      padding:'8px 14px',
                      fontSize:12}}>
                    ✗ Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Rewards list */}
      <div style={{...s.card,marginBottom:20}}>
        <h3 style={{fontSize:15,fontWeight:700,
          color:C.navy,marginBottom:14}}>
          Reward catalog ({rewards.length})
        </h3>
        <div style={{display:'grid',
          gridTemplateColumns:
            'repeat(auto-fill,minmax(180px,1fr))',
          gap:10}}>
          {rewards.map((r:any)=>(
            <div key={r.id}
              style={{padding:'14px',
                borderRadius:12,
                background:C.bg,
                border:`0.5px solid ${C.border}`,
                textAlign:'center'}}>
              <div style={{fontSize:28,
                marginBottom:6}}>
                {r.icon}
              </div>
              <div style={{fontSize:13,
                fontWeight:600,color:C.navy,
                marginBottom:4}}>
                {r.name}
              </div>
              <div style={{fontSize:12,
                fontWeight:700,color:C.amber}}>
                {r.cost_points} pts
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Add reward */}
      <div style={s.card}>
        <h3 style={{fontSize:15,fontWeight:700,
          color:C.navy,marginBottom:14}}>
          Add new reward
        </h3>
        <div style={{display:'grid',
          gridTemplateColumns:'1fr 1fr',
          gap:12,marginBottom:12}}>
          <div>
            <label style={s.label}>
              Reward name
            </label>
            <input style={s.input}
              placeholder="e.g. Ice cream treat"
              value={name}
              onChange={e=>setName(
                e.target.value)}/>
          </div>
          <div>
            <label style={s.label}>
              Cost: {pts} points
            </label>
            <input type="range"
              min={50} max={1000} step={50}
              value={pts}
              onChange={e=>setPts(
                Number(e.target.value))}
              style={{width:'100%',
                marginTop:8,
                accentColor:C.amber}}/>
          </div>
        </div>
        <div style={{marginBottom:14}}>
          <label style={s.label}>Icon</label>
          <div style={{display:'flex',gap:6}}>
            {REWARD_ICONS.map(ic=>(
              <button key={ic}
                onClick={()=>setIcon(ic)}
                style={{
                  fontSize:22,padding:6,
                  borderRadius:8,
                  border:`2px solid ${
                    icon===ic
                      ?C.amber:C.border}`,
                  background:icon===ic
                    ?C.amberL:'white',
                  cursor:'pointer',
                }}>
                {ic}
              </button>
            ))}
          </div>
        </div>
        <button style={s.btn(C.amber)}
          onClick={addReward}
          disabled={!name.trim()}>
          Add Reward
        </button>
      </div>
    </div>
  );
}

// ── REPORT CARD PAGE ──────────────────────────

function ReportPage() {
  const [data,setData] = useState<any>(null);
  const [loading,setLoading] = useState(true);

  useEffect(()=>{
    api.reportCard().then(r=>{
      setData(r.data);
    }).finally(()=>setLoading(false));
  },[]);

  const shareWhatsApp = () => {
    const msg = encodeURIComponent(
      `🌟 ${data?.family_name||'Our family'} ` +
      `scored ${data?.family_score||0}% ` +
      `this week on SuperFit Family!\n\n` +
      `🔥 ${data?.streak_weeks||0} week streak\n\n` +
      `Try it free:\n` +
      `https://app.superfitfamily.in`
    );
    window.open(
      `https://wa.me/?text=${msg}`,'_blank');
  };

  if(loading) return (
    <div style={{color:C.gray,padding:20}}>
      Loading...</div>
  );

  return (
    <div>
      <h1 style={{fontSize:24,fontWeight:800,
        color:C.navy,marginBottom:4}}>
        Report Card
      </h1>
      <p style={{fontSize:13,color:C.gray,
        marginBottom:24}}>
        Share your family's progress
      </p>

      {/* Report card preview */}
      <div id="report-card"
        style={{...s.card,marginBottom:20,
          maxWidth:480}}>
        <div style={{background:C.green,
          margin:'-20px -20px 20px',
          padding:'20px',
          borderRadius:'16px 16px 0 0'}}>
          <Logo size={28} dark/>
          <div style={{fontSize:12,
            color:'rgba(255,255,255,0.8)',
            marginTop:6}}>
            {data?.family_name} · Week Report
          </div>
        </div>

        <div style={{textAlign:'center',
          marginBottom:20}}>
          <div style={{fontSize:13,
            color:C.gray,marginBottom:4}}>
            Family Discipline Score
          </div>
          <div style={{fontSize:48,
            fontWeight:800,color:C.blue}}>
            {data?.family_score||0}%
          </div>
          <div style={{fontSize:12,
            color:C.green,marginTop:4}}>
            🔥 {data?.streak_weeks||0} week
            honest streak
          </div>
        </div>

        {(data?.children||[]).map((c:any)=>(
          <div key={c.child_id}
            style={{marginBottom:12}}>
            <div style={{display:'flex',
              justifyContent:'space-between',
              marginBottom:4,fontSize:13,
              fontWeight:600,color:C.navy}}>
              <span>{c.name}</span>
              <span style={{color:C.blue}}>
                {c.completion_rate}%
              </span>
            </div>
            <div style={{height:8,
              borderRadius:4,
              background:C.border,
              overflow:'hidden'}}>
              <div style={{height:'100%',
                borderRadius:4,
                background:C.blue,
                width:`${c.completion_rate}%`}}/>
            </div>
          </div>
        ))}

        <div style={{borderTop:
          `0.5px solid ${C.border}`,
          paddingTop:14,marginTop:14,
          display:'flex',gap:16,
          flexWrap:'wrap',fontSize:12,
          color:C.gray}}>
          <span>🏃 Exercise: {
            data?.children?.[0]
              ?.exercise_days||0}d</span>
          <span>📖 Reading: {
            data?.children?.[0]
              ?.reading_days||0}d</span>
          <span>💛 Kindness: {
            data?.children?.[0]
              ?.kindness_acts||0}</span>
        </div>

        <div style={{textAlign:'center',
          marginTop:14,fontSize:11,
          color:C.grayL}}>
          app.superfitfamily.in
        </div>
      </div>

      <button
        onClick={shareWhatsApp}
        style={{...s.btn('#25D366'),
          display:'flex',alignItems:'center',
          gap:8,fontSize:15,padding:'14px 24px'}}>
        <span>💬</span>
        Share on WhatsApp
      </button>
    </div>
  );
}

// ── SETTINGS PAGE ─────────────────────────────

function SettingsPage({logout}:any) {
  return (
    <div>
      <h1 style={{fontSize:24,fontWeight:800,
        color:C.navy,marginBottom:4}}>
        Settings
      </h1>
      <p style={{fontSize:13,color:C.gray,
        marginBottom:24}}>
        Manage your family account
      </p>

      <div style={{display:'flex',
        flexDirection:'column',gap:14}}>
        <div style={s.card}>
          <h3 style={{fontSize:15,fontWeight:700,
            color:C.navy,marginBottom:12}}>
            Quick links
          </h3>
          <div style={{display:'flex',
            flexDirection:'column',gap:8}}>
            {[
              {label:'Open mobile app',
               url:'https://app.superfitfamily.in'},
              {label:'Landing page',
               url:'https://superfitfamily.in'},
              {label:'Privacy policy',
               url:'https://superfitfamily.in/privacy.html'},
            ].map(l=>(
              <a key={l.label} href={l.url}
                target="_blank"
                rel="noreferrer"
                style={{fontSize:14,
                  color:C.blue,
                  padding:'10px 0',
                  borderBottom:
                    `0.5px solid ${C.border}`,
                  display:'block',
                  textDecoration:'none'}}>
                {l.label} →
              </a>
            ))}
          </div>
        </div>

        <div style={s.card}>
          <h3 style={{fontSize:15,fontWeight:700,
            color:C.navy,marginBottom:8}}>
            About
          </h3>
          <p style={{fontSize:13,color:C.gray}}>
            SuperFit Family v1.0.0<br/>
            Built for Indian families 🙏<br/>
            No ads · No data selling · Child-safe
          </p>
        </div>

        <button
          onClick={logout}
          style={{...s.btn('#DC2626'),
            padding:'14px',fontSize:15}}>
          Logout
        </button>
      </div>
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────

export default function App() {
  const { authed,fam,login,logout } = useAuth();
  const [page,setPage] = useState('dash');

  if(!authed) return (
    <>
      <Toaster position="top-right"/>
      <Login onLogin={login}/>
    </>
  );

  const pages: Record<string,React.ReactNode> = {
    dash: <DashboardPage/>,
    children: <ChildrenPage/>,
    habits: <HabitsPage/>,
    progress: <ProgressPage/>,
    rewards: <RewardsPage/>,
    report: <ReportPage/>,
    settings: <SettingsPage logout={logout}/>,
  };

  return (
    <div style={{display:'flex',
      minHeight:'100vh'}}>
      <Toaster position="top-right"/>
      <Sidebar page={page} setPage={setPage}
        fam={fam} logout={logout}/>
      <div style={{flex:1,padding:32,
        background:C.bg,overflowY:'auto'}}>
        {pages[page]||<DashboardPage/>}
      </div>
    </div>
  );
}
